import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { completeAdminLogin } from "../api/admin-login-service";
import { authorityFingerprint, setQueryAuthority } from "../api/authority";
import { CyraError } from "../api/errors";
import { identityAdapter, type TotpEnrollment } from "../api/identity-adapter";
import {
  type CurrentActor,
  createCyraSession,
  revokeCurrentCyraSession,
  validateCyraSession,
} from "../api/session-api";
import { clearCyraSession, registerTokenReader, setCyraSession } from "../api/session-credentials";
import {
  initialWorkspaceProgress,
  type WorkspaceProgress,
  warmAdminWorkspace,
} from "../app/warm-workspace";
import { isDemoMode } from "../config/runtime";
import { type DemoIdentity, demoActor, demoIdentityKey, selectedDemoActor } from "./demo-actors";

type AuthStatus =
  | "loading"
  | "warming"
  | "connection-error"
  | "signed-out"
  | "first-factor"
  | "authenticated";

interface AuthContextValue {
  signInError: string;
  actor: CurrentActor | null;
  beginEnrollment: () => Promise<TotpEnrollment>;
  completeEnrollment: (code: string) => Promise<void>;
  login: (email: string, password: string) => Promise<"challenge" | "signed-in">;
  loginWithCode: (email: string, code: string) => Promise<"signed-in">;
  loginWithInvitationToken: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshActor: () => Promise<void>;
  retryWorkspace: () => Promise<void>;
  switchDemoActor?: ((identity: DemoIdentity) => Promise<void>) | undefined;
  status: AuthStatus;
  verifyMfa: (code: string) => Promise<void>;
  workspace: WorkspaceProgress;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const demoAuthKey = "cyra.demo.authenticated";

function isTemporaryConnectionFailure(error: unknown): boolean {
  if (error instanceof CyraError)
    return error.status === 429 || (error.status !== undefined && error.status >= 500);
  return (
    error instanceof TypeError ||
    (error instanceof DOMException && ["AbortError", "TimeoutError"].includes(error.name)) ||
    (typeof error === "object" &&
      error !== null &&
      "code" in error &&
      error.code === "auth/network-request-failed")
  );
}

function signInFailureMessage(error: unknown): string {
  if (
    error instanceof CyraError &&
    (error.status === 429 || error.code === "RATE_LIMIT_EXCEEDED")
  ) {
    const seconds = error.retryAfterSeconds;
    return typeof seconds === "number" && Number.isFinite(seconds) && seconds > 0
      ? `Too many sign-in attempts. Wait ${Math.ceil(seconds)} ${seconds <= 1 ? "second" : "seconds"}, then sign in again.`
      : "Too many sign-in attempts. Wait a moment, then sign in again.";
  }
  return "Admin panel access could not be verified. Try signing in with a staff account, or ask your administrator to check your access.";
}

function authPerformanceMark(name: string): void {
  if (typeof performance !== "undefined" && typeof performance.mark === "function") {
    performance.mark(name);
  }
}

function nextPaint(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === "function") requestAnimationFrame(() => resolve());
    else setTimeout(resolve, 0);
  });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [actor, setActor] = useState<CurrentActor | null>(null);
  const [signInError, setSignInError] = useState("");
  const [workspace, setWorkspace] = useState<WorkspaceProgress>(initialWorkspaceProgress);
  const actorRef = useRef<CurrentActor | null>(null);
  const pageUnloading = useRef(false);
  const providerActive = useRef(false);
  const restorationStarted = useRef(false);
  const workspaceGeneration = useRef(0);
  const authorityRefresh = useRef<Promise<void> | null>(null);
  const recoverTransportFailure = useRef(false);
  const lastAuthorityCheck = useRef(0);

  const establishAuthenticatedActor = useCallback(
    async (currentActor: CurrentActor) => {
      authPerformanceMark("cyra-access-resolution-start");
      const generation = workspaceGeneration.current + 1;
      workspaceGeneration.current = generation;
      const isCurrent = () => providerActive.current && workspaceGeneration.current === generation;
      const actorChanged =
        actorRef.current !== null &&
        authorityFingerprint(actorRef.current) !== authorityFingerprint(currentActor);
      if (actorChanged) {
        setActor(null);
        setStatus("loading");
        setQueryAuthority(null);
        await queryClient.cancelQueries();
        queryClient.clear();
      }
      if (!isCurrent()) return;
      setQueryAuthority(currentActor);
      lastAuthorityCheck.current = Date.now();
      actorRef.current = currentActor;
      setActor(currentActor);
      setStatus("warming");
      setWorkspace({
        completed: 0,
        failures: [],
        label: "Checking permitted admin sections",
        phase: "access",
        total: 0,
      });
      const result = await warmAdminWorkspace({
        actor: currentActor,
        isCurrent,
        onProgress: setWorkspace,
        queryClient,
      });
      if (isDemoMode && isCurrent()) {
        try {
          const { resolveProfileImageAssetId } = await import("../api/media-service");
          const profileImageAssetId = await resolveProfileImageAssetId(null);
          if (!isCurrent()) return;
          const linkedActor = { ...currentActor, profileImageAssetId };
          actorRef.current = linkedActor;
          setActor(linkedActor);
        } catch {
          if (!isCurrent()) return;
          result.failures.push({
            surface: "Profile image",
            message: "The local demo image could not be checked. Reconnect the helper and retry.",
          });
          result.phase = "degraded";
        }
      }
      if (isCurrent()) {
        setWorkspace({ ...result, label: "Preparing workspace", phase: "warming" });
        await nextPaint();
      }
      if (isCurrent()) {
        setWorkspace(result);
        authPerformanceMark("cyra-authenticated-workspace-ready");
        setStatus("authenticated");
      }
    },
    [queryClient],
  );

  useEffect(() => {
    pageUnloading.current = false;
    providerActive.current = true;
    const handlePageHide = () => {
      pageUnloading.current = true;
      providerActive.current = false;
      workspaceGeneration.current += 1;
      lastAuthorityCheck.current = 0;
      setQueryAuthority(null);
      actorRef.current = null;
      setActor(null);
      setStatus("loading");
      void queryClient.cancelQueries();
      queryClient.clear();
    };
    window.addEventListener("pagehide", handlePageHide);
    const deactivateProvider = () => {
      window.removeEventListener("pagehide", handlePageHide);
      providerActive.current = false;
      workspaceGeneration.current += 1;
    };
    if (!isDemoMode && restorationStarted.current) {
      return deactivateProvider;
    }
    restorationStarted.current = true;
    authPerformanceMark("cyra-firebase-restore-start");
    registerTokenReader(() => identityAdapter.currentToken());
    setWorkspace(initialWorkspaceProgress);
    if (isDemoMode) {
      const authenticated = window.localStorage.getItem(demoAuthKey) === "true";
      if (authenticated) void establishAuthenticatedActor(selectedDemoActor());
      else setStatus("signed-out");
      return deactivateProvider;
    }

    setWorkspace({
      completed: 0,
      failures: [],
      label: "Establishing Cyra session",
      phase: "session",
      total: 0,
    });
    authPerformanceMark("cyra-session-validation-start");
    void validateCyraSession()
      .then(async (currentActor) => {
        if (!providerActive.current || pageUnloading.current) return;
        if (!currentActor) {
          setQueryAuthority(null);
          actorRef.current = null;
          setActor(null);
          setStatus("signed-out");
          void queryClient.cancelQueries();
          queryClient.clear();
          await revokeCurrentCyraSession()
            .catch(() => undefined)
            .finally(() => {
              clearCyraSession();
              void identityAdapter.signOut().catch(() => undefined);
            });
          if (!providerActive.current || pageUnloading.current) return;
          setStatus("signed-out");
          return;
        }
        await establishAuthenticatedActor(currentActor);
      })
      .catch((error: unknown) => {
        if (!providerActive.current || pageUnloading.current) return;
        setQueryAuthority(null);
        actorRef.current = null;
        setActor(null);
        void queryClient.cancelQueries();
        queryClient.clear();
        if (isTemporaryConnectionFailure(error)) {
          recoverTransportFailure.current = !(error instanceof CyraError);
          setStatus("connection-error");
          return;
        }
        clearCyraSession();
        void identityAdapter.signOut().catch(() => undefined);
        setStatus("signed-out");
      });
    return deactivateProvider;
  }, [establishAuthenticatedActor, queryClient]);

  const markAuthenticated = useCallback(async () => {
    authPerformanceMark("cyra-identity-complete");
    setStatus("warming");
    setWorkspace({
      completed: 0,
      failures: [],
      label: "Establishing Cyra session",
      phase: "session",
      total: 0,
    });
    try {
      if (!isDemoMode) {
        const token = await identityAdapter.currentToken(true);
        if (!token) throw new Error("Firebase did not issue an identity token.");
        await createCyraSession(token);
        const currentActor = await validateCyraSession();
        if (!currentActor) throw new Error("Staff access could not be verified.");
        await establishAuthenticatedActor(currentActor);
      } else {
        setCyraSession(selectedDemoActor().sessionId);
        window.localStorage.setItem(demoAuthKey, "true");
        await establishAuthenticatedActor(selectedDemoActor());
      }
    } catch (error) {
      setSignInError(signInFailureMessage(error));
      workspaceGeneration.current += 1;
      setQueryAuthority(null);
      actorRef.current = null;
      setActor(null);
      setStatus("signed-out");
      await queryClient.cancelQueries();
      queryClient.clear();
      try {
        await revokeCurrentCyraSession();
      } catch {
        // Local and Firebase authority are still cleared below.
      }
      clearCyraSession();
      await identityAdapter.signOut();
      actorRef.current = null;
      setActor(null);
      setStatus("signed-out");
      throw error;
    }
  }, [establishAuthenticatedActor, queryClient]);

  const login = useCallback(
    async (email: string, password: string) => {
      setSignInError("");
      setQueryAuthority(null);
      actorRef.current = null;
      setActor(null);
      const outcome = await identityAdapter.signIn(email, password);
      if (outcome.mfaRequired) {
        setStatus("first-factor");
        return "challenge";
      }
      await markAuthenticated();
      return "signed-in" as const;
    },
    [markAuthenticated],
  );

  const loginWithCode = useCallback(
    async (email: string, code: string) => {
      setSignInError("");
      setQueryAuthority(null);
      actorRef.current = null;
      setActor(null);
      const outcome = await completeAdminLogin(email, code);
      if (outcome.next !== "SIGNED_IN") throw new Error("Email sign-in could not be completed.");
      await identityAdapter.signInWithCustomToken(outcome.customToken);
      await markAuthenticated();
      return "signed-in" as const;
    },
    [markAuthenticated],
  );

  const verifyMfa = useCallback(
    async (code: string) => {
      await identityAdapter.verifyTotp(code);
      await markAuthenticated();
    },
    [markAuthenticated],
  );
  const loginWithInvitationToken = useCallback(
    async (token: string) => {
      workspaceGeneration.current += 1;
      setSignInError("");
      setQueryAuthority(null);
      actorRef.current = null;
      setActor(null);
      await identityAdapter.signInWithCustomToken(token);
      await markAuthenticated();
    },
    [markAuthenticated],
  );

  const beginEnrollment = useCallback(() => identityAdapter.beginTotpEnrollment(), []);
  const completeEnrollment = useCallback(
    async (code: string) => {
      await identityAdapter.completeTotpEnrollment(code);
      if (!isDemoMode) {
        await identityAdapter.currentToken(true);
        try {
          await revokeCurrentCyraSession();
        } catch {
          clearCyraSession();
        }
      }
      workspaceGeneration.current += 1;
      setQueryAuthority(null);
      actorRef.current = null;
      setActor(null);
      setStatus("signed-out");
      await queryClient.cancelQueries();
      queryClient.clear();
      await identityAdapter.signOut();
      clearCyraSession();
      actorRef.current = null;
      setActor(null);
      setStatus("signed-out");
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    setSignInError("");
    workspaceGeneration.current += 1;
    setQueryAuthority(null);
    actorRef.current = null;
    setActor(null);
    setStatus("signed-out");
    await queryClient.cancelQueries();
    queryClient.clear();
    if (!isDemoMode) {
      try {
        await revokeCurrentCyraSession();
      } catch {
        // Firebase sign-out still clears local authority; the stale backend session cannot be used
        // without its Firebase token and remains subject to normal inactivity expiry.
      }
    }
    await identityAdapter.signOut();
    clearCyraSession();
    window.localStorage.removeItem(demoAuthKey);
    queryClient.clear();
    actorRef.current = null;
    setActor(null);
    setWorkspace(initialWorkspaceProgress);
    setStatus("signed-out");
  }, [queryClient]);

  const refreshActor = useCallback(async () => {
    if (authorityRefresh.current) return authorityRefresh.current;
    const generation = workspaceGeneration.current;
    const isCurrent = () =>
      providerActive.current &&
      !pageUnloading.current &&
      workspaceGeneration.current === generation;
    const refresh = (async () => {
      try {
        const currentActor = isDemoMode
          ? actorRef.current && {
              ...actorRef.current,
              profileImageAssetId: await (
                await import("../api/media-service")
              ).resolveProfileImageAssetId(null),
            }
          : await validateCyraSession();
        if (!isCurrent()) return;
        if (!currentActor) throw new Error("Administrator access could not be verified.");
        if (
          !actorRef.current ||
          authorityFingerprint(actorRef.current) !== authorityFingerprint(currentActor)
        ) {
          await establishAuthenticatedActor(currentActor);
        } else {
          setQueryAuthority(currentActor);
          lastAuthorityCheck.current = Date.now();
          actorRef.current = currentActor;
          setActor(currentActor);
        }
      } catch (error) {
        if (!isCurrent()) return;
        if (isDemoMode) throw error;
        workspaceGeneration.current += 1;
        setQueryAuthority(null);
        actorRef.current = null;
        setActor(null);
        const temporary = isTemporaryConnectionFailure(error);
        recoverTransportFailure.current = temporary && !(error instanceof CyraError);
        setStatus(temporary ? "connection-error" : "signed-out");
        if (!temporary) clearCyraSession();
        await queryClient.cancelQueries();
        queryClient.clear();
        throw error;
      }
    })();
    authorityRefresh.current = refresh;
    try {
      await refresh;
    } finally {
      if (authorityRefresh.current === refresh) authorityRefresh.current = null;
    }
  }, [establishAuthenticatedActor, queryClient]);

  useEffect(() => {
    const restorePage = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      providerActive.current = true;
      pageUnloading.current = false;
      if (isDemoMode) {
        if (window.localStorage.getItem(demoAuthKey) === "true")
          void establishAuthenticatedActor(selectedDemoActor());
        else setStatus("signed-out");
      } else void refreshActor().catch(() => undefined);
    };
    window.addEventListener("pageshow", restorePage);
    return () => window.removeEventListener("pageshow", restorePage);
  }, [establishAuthenticatedActor, refreshActor]);

  useEffect(() => {
    if (isDemoMode || status !== "authenticated") return;
    const refresh = () => {
      if (document.visibilityState === "visible") void refreshActor().catch(() => undefined);
    };
    const interval = window.setInterval(refresh, 30_000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [refreshActor, status]);

  useEffect(() => {
    if (isDemoMode || status !== "connection-error" || !recoverTransportFailure.current) return;
    // One automatic read-only retry per interruption. If it still fails, wait for a real
    // reconnect/focus event or the existing Retry button; never loop or replay a mutation.
    const recover = () => {
      if (
        recoverTransportFailure.current &&
        document.visibilityState === "visible" &&
        navigator.onLine
      )
        void refreshActor().catch(() => undefined);
    };
    const retry = window.setTimeout(recover, 1_000);
    window.addEventListener("online", recover);
    window.addEventListener("focus", recover);
    return () => {
      window.clearTimeout(retry);
      window.removeEventListener("online", recover);
      window.removeEventListener("focus", recover);
    };
  }, [refreshActor, status]);

  const retryWorkspace = useCallback(async () => {
    if (!actorRef.current) return;
    await establishAuthenticatedActor(actorRef.current);
  }, [establishAuthenticatedActor]);

  const switchDemoActor = useCallback(
    async (identity: DemoIdentity) => {
      if (!isDemoMode || status !== "authenticated") return;
      const next = demoActor(identity);
      window.localStorage.setItem(demoIdentityKey, identity);
      setCyraSession(next.sessionId);
      await establishAuthenticatedActor(next);
    },
    [establishAuthenticatedActor, status],
  );

  const value = useMemo(
    () => ({
      actor,
      signInError,
      beginEnrollment,
      completeEnrollment,
      login,
      loginWithCode,
      loginWithInvitationToken,
      logout,
      refreshActor,
      retryWorkspace,
      switchDemoActor: isDemoMode ? switchDemoActor : undefined,
      status,
      verifyMfa,
      workspace,
    }),
    [
      actor,
      signInError,
      beginEnrollment,
      completeEnrollment,
      login,
      loginWithCode,
      loginWithInvitationToken,
      logout,
      refreshActor,
      retryWorkspace,
      switchDemoActor,
      status,
      verifyMfa,
      workspace,
    ],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider.");
  return context;
}
